import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import styles from "./BrandPage.module.scss";
import {
  RiArrowRightUpLine,
  RiArrowRightDownLine,
  RiErrorWarningFill,
} from "react-icons/ri";
import { MdOutlineEdit } from "react-icons/md";

import getclientNameById from "../../../../../components/functions/getclientNameById";
import { RootState } from "../../../../../redux/store";
import { CardMetricKey } from "../AgencyDashboardTabs";
import {
  calculateProgress,
  formatDailyRemaining,
  getMonthShortName,
  getPartnerLogo,
  getYearDataPeriod,
  isNegativeFillColor,
  isPositiveGrowth,
  stripTrailingPhrase,
  toLakhs,
} from "../../../../../components/functions/utilities";
import MetricsModal from "../MetricsModal/MetricsModal";
import { useNavigate } from "react-router-dom";
import { MdOutlineClose } from "react-icons/md";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import { BiTargetLock } from "react-icons/bi";
import { RestS } from "../../../../../rest/rest-service";
import TargetHistoryModal from "../TargetHistoryModal/TargetHistoryModal";
import { BsFillExclamationCircleFill } from "react-icons/bs";
import { VscDebugDisconnect } from "react-icons/vsc";

function GrowthBar({
  pct = 0,
  negative = false,
  ariaLabel,
  showMidLine = false,
  idealPerc = 0,
}: {
  pct?: number;
  negative?: boolean;
  ariaLabel?: string;
  showMidLine?: boolean;
  idealPerc?: number;
}) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const clampedIdeal = Math.max(0, Math.min(100, idealPerc || 0));

  return (
    <div
      className={styles.growthBar}
      role="img"
      aria-label={ariaLabel}
      data-midline={showMidLine ? "true" : "false"}
    >
      <div className={styles.growthBarTrack} />
      <div
        className={styles.growthBarFill}
        data-negative={negative ? "true" : "false"}
        style={{ width: `${clamped}%` }}
      />
      {showMidLine && (
        <div
          className={styles.growthBarIdealLine}
          style={{ left: `${clampedIdeal}%` }}
        />
      )}
    </div>
  );
}

function ChannelBreakdown({
  data,
  sales = true,
  channel,
  brandId,
}: {
  data: any;
  sales?: boolean;
  channel: string;
  brandId?: string | number;
}) {
  const logo = useSelector((state: RootState) =>
    getPartnerLogo(state, data?.ipId)
  );

  const { dailyPerc, dailyRemaining, idealPerc } = calculateProgress(
    data.currMonth,
    data.target
  );

  function getTargetCasesMessege() {
    if (sales) {
      if (dailyPerc < idealPerc - 5) {
        return `You’re ₹${formatDailyRemaining(
          dailyRemaining
        )} behind today's target. Pick up the pace!`;
      } else if (dailyPerc > idealPerc + 5) {
        return `You’re crushing it! Keep up this pace.`;
      } else {
        return `On track. Keep that flow.`;
      }
    } else {
      if (dailyPerc < idealPerc - 5) {
        return `₹${formatDailyRemaining(
          dailyRemaining
        )} of unspent so far. Gearing up?`;
      } else if (dailyPerc > idealPerc + 5) {
        return `Spending quick. Stay sharp.`;
      } else {
        return `Spending smooth.`;
      }
    }
  }
  const [isModalOpen, setIsModalOpen] = useState(false);

  // console.log("DATA IS HEREEEEEEEEE", data);

  return (
    <div className={styles.channelBreakdownComponent}>
      <MetricsModal
        brandId={brandId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={() => console.log("saved")}
        title={`Update ${sales ? "Target" : "Budget"}`}
        data={data}
      />
      <div className={styles.channelBreakdownMetrics}>
        <div className={styles.channelDetails}>
          <div className={styles.channelName}>
            <img
              className={styles.channelIcon}
              src={logo}
              about="channel-logo"
            />
            <span>
              {stripTrailingPhrase(channel, sales ? "Sales" : "Ad Spends")}
            </span>
          </div>
          <div
            className={styles.updateTarget}
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            <MdOutlineEdit className={styles.targetEditIcon} />
            <span className={styles.updateTargetText}>{`Update ${sales ? "Target" : "Budget"
              }`}</span>
          </div>
        </div>
      </div>
      <div className={styles.channelMetrics}>
        <div className={styles.metricGroup}>
          <div className={styles.metricType}>{sales ? "Sales" : "Spent"}</div>
          <div className={styles.metricValue}>{`${toLakhs(
            data?.currMonth
          )} lakhs`}</div>
        </div>

        <div className={styles.metricGroupTwo}>
          <div className={styles.metricType}>{sales ? "Target" : "Budget"}</div>
          {(data?.target || data?.target == 0) && data?.target >= 0 ? (
            <div className={styles.metricValue}>{`${toLakhs(
              data?.target
            )} lakhs`}</div>
          ) : (
            <div className={styles.targetMissing}>
              <RiErrorWarningFill className={styles.warningIcon} />
              <span className={styles.targetMissingText}>Missing</span>
            </div>
          )}
        </div>
      </div>
      {data?.target && data?.target > 0 ? (
        <div className={styles.channelProgressBar}>
          <GrowthBar
            pct={dailyPerc}
            negative={sales ? dailyPerc < idealPerc - 5 : dailyPerc > idealPerc + 5}
            showMidLine={true}
            idealPerc={idealPerc}
          />
          <span className={styles.channelProgressBarPct}>
            {data?.targetPerc?.toFixed(0)}%
          </span>
        </div>
      ) : null}

      {data?.target && data?.target > 0 ? (
        <div className={styles.targetCasesMessege}>
          {getTargetCasesMessege()}
        </div>
      ) : null}
    </div>
  );
}

function BrandPage() {
  const data = useSelector((s: any) => s.agencyDashboard.data);
  const status = useSelector((s: any) => s.agencyDashboard.status);
  const clientsObj = useSelector(
    (s: RootState) => s.agencyConfig.clientsObj ?? {}
  );

  const [isTargetHistoryModalOpen, setIsTargetHistoryModalOpen] =
    useState(false);
  const [targetHistoryData, setTargetHistoryData] = useState<any>(null);
  const [
    isTargetHistoryModalByClientIdOpen,
    setIsTargetHistoryByClientIdModalOpen,
  ] = useState(false);

  if (status !== "SUCCESS") {
    return <Navigate to="/" replace />;
  }
  const navigate = useNavigate();
  const { brandId } = useParams();

  const validBrandIds = React.useMemo(
    () => new Set(Object.keys(data?.cards || {})),
    [data]
  );
  if (!brandId || !validBrandIds.has(brandId)) {
    return <Navigate to="/dashboard" replace />;
  }
  const brandData = data.cards[brandId];
  const brandName = getclientNameById(clientsObj, Number(brandId));

  const getTargetHistoryByClientId = (clientId: number) => {
    RestS.getTargetHistoryByClientId(clientId).then((res) => {
      if (res?.data) {
        setTargetHistoryData(res?.data);
        setIsTargetHistoryModalOpen(true);
      }
      // console.log("DATATATATATTATA", res?.data);
    });
  };

  const getMonthYearText = () => {
    const startRaw = data?.jobDetails?.dataPeriod?.[0];
    const endRaw = data?.jobDetails?.dataPeriod?.[1];

    if (!startRaw || !endRaw) return "";

    // Convert "DD-MM-YYYY" → Date object
    const [startDay, startMonth, startYear] = startRaw.split("-").map(Number);
    const [endDay, endMonth, endYear] = endRaw.split("-").map(Number);

    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid date range";
    }

    const startMonthName = start.toLocaleString("en-IN", { month: "short" });
    const endMonthName = end.toLocaleString("en-IN", { month: "short" });
    const startDayStr = String(start.getDate()).padStart(2, "0");
    const endDayStr = String(end.getDate()).padStart(2, "0");

    if (startYear === endYear) {
      return `${startMonthName} ${startDayStr} – ${endMonthName} ${endDayStr}, ${endYear}`;
    }

    return `${startMonthName} ${startDayStr}, ${startYear} – ${endMonthName} ${endDayStr}, ${endYear}`;
  };

  // useEffect(() => console.log("mounted"));

  return (
    <div className={styles.pageBody}>
      <TargetHistoryModal
        targetHistoryData={targetHistoryData}
        isTargetHistoryByClientId={true}
        isTargetHistoryModalOpen={isTargetHistoryModalOpen}
        setIsTargetHistoryModalOpen={setIsTargetHistoryModalOpen}
      />
      <div className={styles.pageHeader}>
        <div className={styles.monthYear}>{getMonthYearText()}</div>
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContainerr}>
            <div className={styles.brandName}>{brandName}</div>
            <div className={styles.rightContainer}>
              <MeritoButton
                type="outline-dim"
                size="md"
                icon={<BiTargetLock />}
                btnText="Target History"
                onClick={() => getTargetHistoryByClientId(Number(brandId))}
              />
              <MeritoButton
                type="destructive-tint"
                size="md"
                icon={<MdOutlineClose />}
                onlyIcon={true}
                onClick={() => navigate(-1)}
              />
            </div>
          </div>

          <div className={styles.brandKpis}>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>Sales</div>
              {!brandData[CardMetricKey.Sales] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>
                    {`${brandData[CardMetricKey.Sales]?.unit}${toLakhs(
                      brandData[CardMetricKey.Sales]?.currMonth
                    )}
                lakhs`}
                  </div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.Sales]?.growth?.type === "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.Sales]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.Sales]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.Sales]?.growth?.value === "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.Sales]?.growth?.value
                        )}%`}
                    </div>
                  </div>
                  {brandData[CardMetricKey.Sales]?.targetPerc ? (
                    <div className={styles.kpiGrowthBar}>
                      <div className={styles.growthBarContainer}>
                        <GrowthBar
                          pct={brandData[CardMetricKey.Sales]?.targetPerc}
                          negative={isNegativeFillColor(
                            brandData[CardMetricKey.Sales]?.currMonth,
                            "sales",
                            brandData[CardMetricKey.Sales]?.target
                          )}
                          ariaLabel="Sales Growth"
                        />
                      </div>
                      <span className={styles.targetPerc}>{`${brandData[
                        CardMetricKey.Sales
                      ]?.targetPerc?.toFixed(0)}%`}</span>
                    </div>
                  ) : brandData[CardMetricKey.Sales]?.target !== 0 ? (
                    <div className={styles.metricMissing}>Target missing</div>
                  ) : null}
                </>
              )}
            </div>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>Ad Spends</div>
              {!brandData[CardMetricKey.AdSpends] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>{`${brandData[CardMetricKey.AdSpends]?.unit
                    }${toLakhs(brandData[CardMetricKey.AdSpends]?.currMonth)}
                lakhs`}</div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.AdSpends]?.growth?.type ===
                        "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.AdSpends]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.AdSpends]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.AdSpends]?.growth?.value === "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.AdSpends]?.growth?.value
                        )}%`}
                    </div>
                  </div>
                  {brandData[CardMetricKey.AdSpends]?.target ? (
                    <div className={styles.kpiGrowthBar}>
                      <div className={styles.growthBarContainer}>
                        <GrowthBar
                          pct={brandData[CardMetricKey.AdSpends]?.targetPerc}
                          negative={isNegativeFillColor(
                            brandData[CardMetricKey.AdSpends]?.currMonth,
                            "spends",
                            brandData[CardMetricKey.AdSpends]?.target
                          )}
                          ariaLabel="Sales Growth"
                        />
                      </div>
                      <span className={styles.targetPerc}>
                        {`${brandData[
                          CardMetricKey.AdSpends
                        ]?.targetPerc.toFixed(0)}%`}
                      </span>
                    </div>
                  ) : brandData[CardMetricKey.AdSpends]?.target !== 0 ? (
                    <div className={styles.metricMissing}>Budget missing</div>
                  ) : null}
                </>
              )}
            </div>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>ROAS</div>
              {!brandData[CardMetricKey.ROAS] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>
                    {brandData[CardMetricKey.ROAS]?.currMonth
                      .toFixed(1)
                      .toString()
                      .replace(/\.0+$/, "")}
                  </div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.ROAS]?.growth?.type === "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.ROAS]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.ROAS]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.ROAS]?.growth.value === "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.ROAS]?.growth.value
                        )}%`}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>New Customers</div>
              {!brandData[CardMetricKey.NewCustomers] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>
                    {brandData[CardMetricKey.NewCustomers]?.currMonth}
                  </div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.NewCustomers]?.growth?.type ===
                        "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.NewCustomers]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.NewCustomers]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.NewCustomers]?.growth?.value ===
                        "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.NewCustomers]?.growth?.value
                        )}%`}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>Orders</div>
              {!brandData[CardMetricKey.Orders] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>
                    {brandData[CardMetricKey.Orders]?.currMonth}
                  </div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.Orders]?.growth?.type ===
                        "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.Orders]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.Orders]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.Orders]?.growth?.value === "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.Orders]?.growth?.value
                        )}%`}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.kpiItem}>
              <div className={styles.kpiName}>Cost Per Order</div>
              {!brandData[CardMetricKey.CostPerOrder] ? (
                <div className={styles.noDataMetric}>-</div>
              ) : (
                <>
                  <div className={styles.kpiValue}>
                    {`${brandData[CardMetricKey.CostPerOrder]?.unit}${brandData[
                      CardMetricKey.CostPerOrder
                    ]?.currMonth
                      .toFixed(1)
                      .toString()
                      .replace(/\.0+$/, "")}`}
                  </div>
                  <div
                    className={styles.kpiGrowth}
                    data-negative={
                      brandData[CardMetricKey.CostPerOrder]?.growth?.type ===
                        "NEUTRAL"
                        ? "neutral"
                        : isPositiveGrowth(
                          brandData[CardMetricKey.CostPerOrder]?.growth
                        )
                          ? "false"
                          : "true"
                    }
                  >
                    {brandData[CardMetricKey.CostPerOrder]?.growth?.type ===
                      "INCREASE" ? (
                      <RiArrowRightUpLine />
                    ) : (
                      <RiArrowRightDownLine />
                    )}

                    <div className={styles.kpiGrowthValue}>
                      {brandData[CardMetricKey.CostPerOrder]?.growth?.value ===
                        "-"
                        ? "-"
                        : `${Math.abs(
                          brandData[CardMetricKey.CostPerOrder]?.growth?.value
                        )}%`}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={styles.targets}>
            <div>Targets</div>
            <div className={styles.targetLine}></div>
          </div>
          <div className={styles.channelBreakdown}>
            <div className={styles.channelBreakdownItem}>
              <div className={styles.channelBreakdownTitle}>Sales</div>
              {!brandData[CardMetricKey.Sales] && (
                <div className={styles.noDataMetricContainer}>
                  <BsFillExclamationCircleFill
                    className={styles.circleFillIcon}
                  />
                  <div className={styles.noDataetricText}>
                    No Sales Account Connected!
                  </div>
                  <MeritoButton
                    type="primary-1"
                    size="md"
                    btnText="Connect Now"
                    icon={<VscDebugDisconnect />}
                    onClick={() => navigate(`/workspaceStore?cid=${brandId}`)}
                  />
                </div>
              )}
              <div className={styles.channelBreakdownList}>
                {Object.entries(brandData[CardMetricKey.Sales]?.channels).map(
                  ([channel, data]: [string, any]) => {
                    return (
                      <ChannelBreakdown
                        brandId={brandId}
                        key={channel}
                        data={data}
                        sales={true}
                        channel={channel}
                      />
                    );
                  }
                )}
              </div>
            </div>
            <div className={styles.channelBreakdownItem}>
              <div className={styles.channelBreakdownTitle}>Ad Spends</div>
              {!brandData[CardMetricKey.AdSpends] && (
                <div className={styles.noDataMetricContainer}>
                  <BsFillExclamationCircleFill
                    className={styles.circleFillIcon}
                  />
                  <div className={styles.noDataetricText}>
                    No Ads Account Connected!
                  </div>
                  <MeritoButton
                    type="primary-1"
                    size="md"
                    btnText="Connect Now"
                    icon={<VscDebugDisconnect />}
                    onClick={() => navigate(`/workspaceStore?cid=${brandId}`)}
                  />
                </div>
              )}
              <div className={styles.channelBreakdownList}>
                {Object.entries(
                  brandData[CardMetricKey.AdSpends]?.channels || {}
                ).map(([channel, data]: [string, any]) => {
                  return (
                    <ChannelBreakdown
                      brandId={brandId}
                      key={channel}
                      data={data}
                      sales={false}
                      channel={channel}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandPage;
